import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    session: null,
    loading: true,
    signOut: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();

    const handleAuthError = async (error: AuthError | null, source: string) => {
        if (!error) return;

        console.error(`Auth error in ${source}:`, error);

        // Check for specific error conditions that indicate a bad session
        const isRefreshTokenError = error.message?.includes("Invalid Refresh Token") ||
            error.message?.includes("Refresh Token Not Found");

        // 400 errors during session refresh usually mean the token is invalid/expired/revoked
        // and cannot be recovered.
        const isBadSession = isRefreshTokenError || (error.status === 400 && source === 'getSession');

        if (isBadSession) {
            await cleanLogout();
        }
    };

    const cleanLogout = async () => {
        try {
            // Attempt standard sign out
            await supabase.auth.signOut();
        } catch (e) {
            console.error('Error during sign out:', e);
        } finally {
            // Force local cleanup
            setUser(null);
            setSession(null);

            // Clear all supabase cookies manually if needed (cookieStorage handles most)
            // but let's be safe and ensure state is reset

            if (location.pathname !== '/auth') {
                navigate('/auth', { replace: true });
                toast({
                    title: "Session Expired",
                    description: "Please sign in again.",
                    variant: "destructive",
                });
            }
        }
    };

    useEffect(() => {
        let mounted = true;

        // Initial session check
        const checkSession = async () => {
            try {
                const { data, error } = await supabase.auth.getSession();

                if (error) {
                    await handleAuthError(error, 'getSession');
                } else if (mounted) {
                    setSession(data.session);
                    setUser(data.session?.user ?? null);
                }
            } catch (err: any) {
                await handleAuthError(err, 'checkSession_catch');
            } finally {
                if (mounted) setLoading(false);
            }
        };

        checkSession();

        // Listen for changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
            if (!mounted) return;

            if (event === 'SIGNED_OUT') {
                setUser(null);
                setSession(null);
                setLoading(false);
                // cleanLogout() might cause a loop if triggered by signOut itself, 
                // so we just reset state here. The signOut method will handle navigation.
            } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
                setSession(newSession);
                setUser(newSession?.user ?? null);
                setLoading(false);
            } else if (event === 'INITIAL_SESSION') {
                // Handled by getSession above usually, but good fallback
                setSession(newSession);
                setUser(newSession?.user ?? null);
                setLoading(false);
            }
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const signOut = async () => {
        await cleanLogout();
    };

    return (
        <AuthContext.Provider value={{ user, session, loading, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};
